import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const Locations = () => {
  const locations = [
    {
      name: 'Mumbai',
      description: 'Iconic skyline living and premium high-rises',
      image: 'https://images.unsplash.com/photo-1666289158111-7576ce2ccfae?crop=entropy&cs=srgb&fm=jpg&q=85',
      gridClass: 'md:col-span-8',
    },
    {
      name: 'Alibaug',
      description: 'Serene coastal villas and beach homes',
      image: 'https://images.unsplash.com/photo-1728486888025-34e8d66b6b7b?crop=entropy&cs=srgb&fm=jpg&q=85',
      gridClass: 'md:col-span-4',
    },
    {
      name: 'Dubai',
      description: 'Architectural marvels and global luxury',
      image: 'https://images.unsplash.com/photo-1748373448914-1d7f882700e2?crop=entropy&cs=srgb&fm=jpg&q=85',
      gridClass: 'md:col-span-4',
    },
    {
      name: 'Lonavala',
      description: 'Hill-station retreats and weekend sanctuaries',
      image: 'https://images.unsplash.com/photo-1644620426342-471c3ed4e1d4?crop=entropy&cs=srgb&fm=jpg&q=85',
      gridClass: 'md:col-span-8',
    },
  ];

  return (
    <section id="locations" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-background" data-testid="locations-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-secondary text-sm font-medium tracking-widest uppercase">Featured Destinations</span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-primary mt-4 mb-6 tracking-tight">
            Luxury Across Coveted Locations
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            From Mumbai's metropolitan elegance to international sophistication, explore our curated portfolio
            across the world's most prestigious addresses.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {locations.map((location, index) => (
            <Link
              key={index}
              to={`/properties?location=${location.name}`}
              data-testid={`location-${location.name.toLowerCase()}`}
              className={`group relative overflow-hidden cursor-pointer ${location.gridClass} h-[400px]`}
            >
              {/* Image */}
              <img
                src={location.image}
                alt={location.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />

              {/* Border */}
              <div className="absolute inset-0 border border-secondary/30 group-hover:border-secondary transition-colors duration-500" />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                >
                  <h3 className="font-playfair text-3xl lg:text-4xl font-bold text-primary-foreground mb-2">
                    {location.name}
                  </h3>
                  <p className="text-primary-foreground/80 text-sm mb-4">{location.description}</p>
                  <div className="flex items-center gap-2 text-secondary text-sm font-medium group-hover:gap-4 transition-all duration-300">
                    Explore Properties
                    <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </motion.div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <a
            href="/contact"
            data-testid="locations-inquiry-cta"
            className="inline-block bg-secondary text-secondary-foreground hover:bg-[#C5A028] px-8 py-6 font-manrope font-bold tracking-wide uppercase text-xs transition-all duration-300"
          >
            Request Location Consultation
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Locations;
